// Client-side logging shipped to the server (files + Seq). analytics.ts is for funnels; this is for
// diagnosing what happened to one user, with full messages, context and stack traces next to the
// server's half of the request.
//
// Calls never throw or block the UI. Entries flush in small batches on a timer, immediately on error,
// and on page-hide via navigator.sendBeacon so a closed tab still delivers.

type Level = 'debug' | 'info' | 'warn' | 'error';

interface Entry {
  level: Level;
  message: string;
  context?: string;
  url: string;
  at: number;
}

const ENDPOINT = '/api/ClientLog';
const FLUSH_INTERVAL_MS = 3000;
const MAX_BUFFER = 20;

let buffer: Entry[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let installed = false;

function stringifyContext(context?: unknown): string | undefined {
  if (context == null) {
    return undefined;
  }
  try {
    return typeof context === 'string' ? context : JSON.stringify(context);
  } catch {
    return String(context);
  }
}

function scheduleFlush(): void {
  if (timer != null) {
    return;
  }
  timer = setTimeout(() => {
    timer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

// Drains the buffer. `useBeacon` is set on page-hide, where fetch may be cancelled; sendBeacon survives.
function flush(useBeacon = false): void {
  if (buffer.length === 0) {
    return;
  }
  const entries = buffer;
  buffer = [];
  if (timer != null) {
    clearTimeout(timer);
    timer = null;
  }
  const body = JSON.stringify({ entries });
  try {
    if (useBeacon && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    }
    // keepalive lets the request outlive a same-tab navigation too.
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      /* logging must never surface its own failure */
    });
  } catch {
    /* swallow — a logging failure must not become an app failure */
  }
}

function push(level: Level, message: string, context?: unknown): void {
  try {
    buffer.push({
      level,
      message,
      context: stringifyContext(context),
      url: typeof location !== 'undefined' ? location.href : '',
      at: Date.now(),
    });
    // Errors and a full buffer flush now; the rest is batched on the timer.
    if (level === 'error' || buffer.length >= MAX_BUFFER) {
      flush();
    } else {
      scheduleFlush();
    }
  } catch {
    /* never throw from a log call */
  }
}

// Install once from the entry point: captures uncaught errors and unhandled rejections, and flushes
// on tab close.
function install(): void {
  if (installed || typeof window === 'undefined') {
    return;
  }
  installed = true;

  window.addEventListener('error', (e: ErrorEvent) => {
    push('error', `Uncaught: ${e.message}`, {
      source: e.filename,
      line: e.lineno,
      col: e.colno,
      stack: e.error?.stack,
    });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    push('error', 'Unhandled rejection', {
      message: reason?.message ?? String(reason),
      stack: reason?.stack,
    });
  });

  // pagehide covers the mobile / back-forward cache case that visibilitychange+unload miss.
  window.addEventListener('pagehide', () => flush(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush(true);
    }
  });
}

export const clientLog = {
  install,
  debug: (message: string, context?: unknown) => push('debug', message, context),
  info: (message: string, context?: unknown) => push('info', message, context),
  warn: (message: string, context?: unknown) => push('warn', message, context),
  error: (message: string, context?: unknown) => push('error', message, context),
};
